import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import {
  getSuggestedPrompts,
  sendAccuracyGuidelineChatMessage,
  type AccuracyChatMessage,
  type AccuracyGuidelineChatContext,
} from '../services/accuracyGuidelineChat';
import { buildWelcomeSampleProposal } from '../services/accuracyGuidelineProposals';
import { DRILLDOWN_LABELS } from '../services/accuracyCountDrilldown';
import GuidelineDiffBlock from './GuidelineDiffBlock';
import './AccuracyGuidelineChatSidebar.css';

interface AccuracyGuidelineChatSidebarProps {
  context: AccuracyGuidelineChatContext;
  onApplyProposal?: (proposal: NonNullable<AccuracyChatMessage['proposal']>) => void;
}

function newMessage(
  role: 'user' | 'assistant',
  content: string,
  proposal?: AccuracyChatMessage['proposal']
): AccuracyChatMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    proposal,
    createdAt: new Date().toISOString(),
  };
}

function AssistantProposalBody({
  proposal,
  onApply,
}: {
  proposal: NonNullable<AccuracyChatMessage['proposal']>;
  onApply?: () => void;
}) {
  return (
    <>
      <p className="accuracy-chat-proposal-summary">{proposal.summary}</p>
      {proposal.hunks.map((hunk, i) => (
        <GuidelineDiffBlock key={`${hunk.pathLabel}-${hunk.field}-${i}`} hunk={hunk} />
      ))}
      {proposal.hunks.length > 0 && onApply ? (
        <button type="button" className="accuracy-button primary accuracy-chat-apply-btn" onClick={onApply}>
          この提案をドラフトに反映
        </button>
      ) : null}
      {proposal.hunks.length > 0 && (
        <p className="accuracy-chat-mock-note">
          ※ モック diff です。実装時は guidelines.json の該当バージョン・カテゴリから LLM が行単位で提案します。
        </p>
      )}
    </>
  );
}

const AccuracyGuidelineChatSidebar: React.FC<AccuracyGuidelineChatSidebarProps> = ({
  context,
  onApplyProposal,
}) => {
  const [messages, setMessages] = useState<AccuracyChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const suggestedPrompts = getSuggestedPrompts();
  const countLabel = DRILLDOWN_LABELS.countKind[context.drilldown.countKind];
  const welcomeSample = buildWelcomeSampleProposal(context);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
  }, [
    context.reportId,
    context.drilldown.categoryId,
    context.drilldown.metricType,
    context.drilldown.countKind,
  ]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      setError(null);
      const userMsg = newMessage('user', trimmed);
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setLoading(true);

      try {
        const proposal = await sendAccuracyGuidelineChatMessage(context, messages, trimmed);
        setMessages((prev) => [
          ...prev,
          newMessage('assistant', proposal.summary, proposal),
        ]);
      } catch (e) {
        setError(e instanceof Error ? e.message : '送信に失敗しました');
      } finally {
        setLoading(false);
        textareaRef.current?.focus();
      }
    },
    [context, loading, messages]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  return (
    <aside className="accuracy-chat-sidebar" aria-label="ガイドライン改善チャット">
      <header className="accuracy-chat-header">
        <div className="accuracy-chat-header-title">
          <MessageSquare size={18} />
          <h2>ガイドライン改善</h2>
        </div>
        <p className="accuracy-chat-header-desc">
          {context.errorSourceLabel
            ? `${context.errorSourceLabel} の誤り（${context.totalItemCount} 件）をもとに改善案を相談（モック）`
            : `${context.drilldown.categoryName} · ${countLabel} の改善案を LLM に相談（モック）`}
        </p>
      </header>

      <div className="accuracy-chat-messages" ref={listRef}>
        {messages.length === 0 && !loading && (
          <div className="accuracy-chat-welcome">
            <p>
              誤検知の削減や検知漏れの検出改善のため、ガイドラインの編集案を提案してもらえます。
              変更箇所は GitHub 風の行 diff で表示します（<code>existence_check_prompt</code> /{' '}
              <code>violation_check_prompt</code>）。
            </p>
            {welcomeSample && (
              <div className="accuracy-chat-welcome-sample">
                <p className="accuracy-chat-welcome-sample-label">変更例（プレビュー）</p>
                <AssistantProposalBody proposal={welcomeSample} />
              </div>
            )}
            <div className="accuracy-chat-prompts">
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="accuracy-chat-prompt-chip"
                  onClick={() => void sendMessage(prompt)}
                  disabled={loading}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`accuracy-chat-bubble accuracy-chat-bubble-${msg.role}`}
          >
            <div className="accuracy-chat-bubble-role">
              {msg.role === 'user' ? 'あなた' : 'アシスタント'}
            </div>
            <div className="accuracy-chat-bubble-body">
              {msg.role === 'assistant' && msg.proposal ? (
                <AssistantProposalBody
                  proposal={msg.proposal}
                  onApply={
                    onApplyProposal && msg.proposal.hunks.length > 0
                      ? () => onApplyProposal(msg.proposal!)
                      : undefined
                  }
                />
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="accuracy-chat-bubble accuracy-chat-bubble-assistant">
            <div className="accuracy-chat-bubble-role">アシスタント</div>
            <div className="accuracy-chat-loading">
              <Loader2 size={16} className="spin" />
              提案を作成しています…
            </div>
          </div>
        )}
      </div>

      {error && <div className="accuracy-chat-error">{error}</div>}

      <form className="accuracy-chat-form" onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          className="accuracy-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="例: 誤検知を減らすにはガイドラインのどこを直すべきですか？"
          rows={3}
          disabled={loading}
        />
        <div className="accuracy-chat-form-actions">
          <Link to="/guidelines" className="accuracy-chat-guidelines-link">
            ガイドライン管理へ
          </Link>
          <button type="submit" className="accuracy-chat-send" disabled={loading || !input.trim()}>
            {loading ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            送信
          </button>
        </div>
      </form>
    </aside>
  );
};

export default AccuracyGuidelineChatSidebar;
